import { useIntl } from 'react-intl';
import {
  IconFile,
  IconFileTypeCsv,
  IconFileTypeXls,
  IconFileTypeXml,
  IconHelpCircle,
  IconJson,
} from '@rossum/ui/icons/tabler';
import {
  Checkbox,
  Divider,
  MenuItem,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { docsLinks } from '../../../constants/values';
import { LocalizationKeys } from '../../../i18n';
import { boldText } from '../../../lib/formaterValues';
import { useLocalStorageBooleanFlag } from '../../../lib/hooks';
import { DOCUMENTS_EXPORT_WITH_DOWNLOAD } from '../../../redux/modules/localStorage/actions';
import {
  ExportMutationContext,
  exportMutationIcons,
  ExportMutationIneligibilityReason,
  ExportMutationState,
} from '../annotation-actions/config';
import { DownloadFormat, FORMATS } from './formats';

// BE limit for bulk download
const MAX_PAGE_SIZE_LIMIT = 500;

const formatIcons: Record<DownloadFormat, React.FC> = {
  xlsx: IconFileTypeXls,
  csv: IconFileTypeCsv,
  xml: IconFileTypeXml,
  json: IconJson,
};

const iconSx = { mr: 1, flexShrink: 0 } as const;

const dividerSx = {
  '&.MuiDivider-root': { marginTop: 0, marginBottom: 0 },
} as const;

const ineligibilityReasonMessageId: Record<
  ExportMutationIneligibilityReason,
  LocalizationKeys
> = {
  mixed_status: 'features.documentList.exportMenu.ineligible.mixedStatus',
  not_exportable_status:
    'features.documentList.exportMenu.ineligible.notExportableStatus',
  restricted_access:
    'features.documentList.exportMenu.ineligible.restrictedAccess',
  hide_export_button:
    'features.documentList.exportMenu.ineligible.hideExportButton',
};

const mutationLabelMessageId: Record<ExportMutationContext, LocalizationKeys> =
  {
    confirmed: 'features.documentList.exportMenu.moveToExports',
    failed_export: 'features.documentList.exportMenu.reExport',
  };

const mutationWithDownloadLabelMessageId: Record<
  ExportMutationContext,
  LocalizationKeys
> = {
  confirmed: 'features.documentList.exportMenu.moveToExportsWithDownload',
  failed_export: 'features.documentList.exportMenu.reExportWithDownload',
};

type ExportMenuContentProps = {
  exportMutationState: ExportMutationState;
  onDownload: (format: DownloadFormat) => void;
  // No `format` — export only, no file download.
  onExport?: (format?: DownloadFormat) => void;
  onAsyncDownload?: () => void;
  selectedItemsCount: number;
};

const ExportMenuContent = ({
  exportMutationState,
  onDownload,
  onExport,
  onAsyncDownload,
  selectedItemsCount,
}: ExportMenuContentProps) => {
  const intl = useIntl();
  const [exportWithDownload, setExportWithDownload] =
    useLocalStorageBooleanFlag(DOCUMENTS_EXPORT_WITH_DOWNLOAD, false);

  const showMutationRow =
    exportMutationState.visible && typeof onExport === 'function';
  const mutationContext = exportMutationState.visible
    ? exportMutationState.context
    : null;
  const mutationDisabled =
    exportMutationState.visible && exportMutationState.disabled;

  const PrimaryIcon = mutationContext
    ? exportMutationIcons[mutationContext]
    : null;

  const showWithDownloadCheckbox = showMutationRow && !mutationDisabled;

  return (
    <>
      {/* Primary export mutation action */}
      {showMutationRow && PrimaryIcon && mutationContext ? (
        <>
          <Tooltip
            title={
              exportMutationState.visible && mutationDisabled ? (
                <Stack spacing={0.5}>
                  <Typography variant="caption">
                    {intl.formatMessage(
                      {
                        id: 'features.documentList.exportMenu.ineligible.title',
                      },
                      {
                        ineligibleCount: exportMutationState.ineligibleCount,
                        totalCount: exportMutationState.totalCount,
                      }
                    )}
                  </Typography>
                  {exportMutationState.ineligible.map(({ reason, count }) => (
                    <Typography key={reason} variant="caption">
                      {intl.formatMessage(
                        { id: ineligibilityReasonMessageId[reason] },
                        { count }
                      )}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                ''
              )
            }
          >
            {/* span lets the tooltip register hover even when MenuItem is disabled */}
            <span>
              <MenuItem
                disabled={mutationDisabled}
                onClick={mutationDisabled ? undefined : () => onExport?.()}
                data-cy="export-menu-primary"
              >
                <SvgIcon fontSize="large" sx={iconSx}>
                  <PrimaryIcon />
                </SvgIcon>
                <Typography variant="body2">
                  {intl.formatMessage(
                    { id: mutationLabelMessageId[mutationContext] },
                    { bold: boldText }
                  )}
                </Typography>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider sx={dividerSx} />
        </>
      ) : null}

      {/* Download format items */}
      {FORMATS.map((format: DownloadFormat) => {
        const FormatIcon = formatIcons[format];
        return (
          <MenuItem
            key={format}
            onClick={() => {
              if (showMutationRow && !mutationDisabled && exportWithDownload) {
                // Combined: status change + file download in a single request.
                onExport?.(format);
              } else {
                onDownload(format);
              }
            }}
            data-cy={`export-menu-format-${format}`}
          >
            <SvgIcon fontSize="large" sx={iconSx}>
              <FormatIcon />
            </SvgIcon>
            <Typography variant="body2">
              {intl.formatMessage({
                id: `features.documentList.exportMenu.download.${format}`,
              })}
            </Typography>
          </MenuItem>
        );
      })}

      {onAsyncDownload ? (
        <Tooltip
          title={
            selectedItemsCount > MAX_PAGE_SIZE_LIMIT
              ? intl.formatMessage(
                  { id: 'containers.documents.formats.all.disabled' },
                  { count: MAX_PAGE_SIZE_LIMIT }
                )
              : ''
          }
        >
          <MenuItem
            onClick={
              selectedItemsCount <= MAX_PAGE_SIZE_LIMIT
                ? () => {
                    if (
                      showMutationRow &&
                      !mutationDisabled &&
                      exportWithDownload
                    ) {
                      onExport?.();
                    }
                    onAsyncDownload();
                  }
                : undefined
            }
            data-cy="export-menu-format-original"
          >
            <SvgIcon fontSize="large" sx={iconSx}>
              <IconFile />
            </SvgIcon>
            <Typography variant="body2">
              {intl.formatMessage({
                id: 'features.documentList.exportMenu.download.original',
              })}
            </Typography>
          </MenuItem>
        </Tooltip>
      ) : null}

      {/* Side-effect checkbox */}
      {showWithDownloadCheckbox && mutationContext ? (
        <MenuItem
          disableRipple
          onClick={() => setExportWithDownload(!exportWithDownload)}
          data-cy="export-menu-checkbox"
          sx={{ py: 1 }}
        >
          <Checkbox checked={exportWithDownload} sx={{ p: 0, mr: 1 }} />
          <Typography variant="body2">
            {intl.formatMessage(
              { id: mutationWithDownloadLabelMessageId[mutationContext] },
              { bold: boldText }
            )}
          </Typography>
        </MenuItem>
      ) : null}

      {/* API docs link */}
      <Divider sx={dividerSx} />
      <MenuItem
        onClick={() => window.open(docsLinks.export, '_blank', 'noopener')}
      >
        <SvgIcon fontSize="large" sx={iconSx}>
          <IconHelpCircle />
        </SvgIcon>
        <Typography variant="body2">
          {intl.formatMessage({
            id: 'features.documentList.exportMenu.howToUseApi',
          })}
        </Typography>
      </MenuItem>
    </>
  );
};

export { ExportMenuContent };
